import React from 'react'
import type { SalesDeck, SalesDeckSlide } from '../../content/salesDecks'
import { ProductScreenVisual, type ProductScreenKey } from '../landing/ProductScreenVisual'
import '../../styles/salesDecks.css'

type SalesDeckRendererProps = {
  deck: SalesDeck
}

const getSlideAnchor = (slide: SalesDeckSlide) => `slide-${slide.id}`

const inferScreenKey = (slide: SalesDeckSlide): ProductScreenKey => {
  if (slide.visualKey) return slide.visualKey
  const haystack = `${slide.id} ${slide.eyebrow} ${slide.title} ${slide.message} ${slide.visualSuggestion ?? ''}`.toLowerCase()
  if (haystack.includes('commission') || haystack.includes('referral') || haystack.includes('pipeline') || haystack.includes('revenue')) return 'sales-pipeline'
  if (haystack.includes('payroll') || haystack.includes('export') || haystack.includes('settlement') || haystack.includes('invoice')) return 'payroll-export'
  if (haystack.includes('timesheet') || haystack.includes('approval') || haystack.includes('dispute')) return 'timesheet-approvals'
  if (haystack.includes('document') || haystack.includes('compliance') || haystack.includes('credential') || haystack.includes('eligibility')) return 'worker-documents'
  if (haystack.includes('mobile') || haystack.includes('punch') || haystack.includes('break')) return 'worker-mobile'
  if (haystack.includes('map') || haystack.includes('geofence') || haystack.includes('site') || haystack.includes('post')) return 'geofence-map'
  if (haystack.includes('schedule') || haystack.includes('calendar') || haystack.includes('shift')) return 'schedule-board'
  if (haystack.includes('worker profile') || haystack.includes('roster') || haystack.includes('reliability')) return 'worker-profile'
  if (haystack.includes('relationship') || haystack.includes('sharing') || haystack.includes('client') || haystack.includes('agency')) return 'network-relationships'
  if (haystack.includes('job') || haystack.includes('order') || haystack.includes('assignment')) return 'jobs-orders'
  return 'network-dashboard'
}

const renderSlide = (slide: SalesDeckSlide, index: number, total: number) => {
  const screenKey = inferScreenKey(slide)

  return (
    <section className="sales-deck-slide" id={getSlideAnchor(slide)} key={slide.id}>
      <div className="sales-deck-slide__meta">
        <span>{slide.eyebrow}</span>
        <span>{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
      </div>
      <div className="sales-deck-slide__body">
        <div>
          <h2>{slide.title}</h2>
          <p className="sales-deck-slide__message">{slide.message}</p>
        </div>
        <ul className="sales-deck-slide__bullets">
          {slide.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
      {(slide.visualSuggestion || slide.speakerNotes) && (
        <div className="sales-deck-slide__support">
          {slide.visualSuggestion && (
            <aside className="sales-deck-callout sales-deck-callout--screen">
              <span>Product screen reference</span>
              <ProductScreenVisual variant={screenKey} compact className="sales-deck-product-screen" />
              <p>{slide.visualSuggestion}</p>
            </aside>
          )}
          {slide.speakerNotes && (
            <details className="sales-deck-notes">
              <summary>Speaker notes</summary>
              <p>{slide.speakerNotes}</p>
            </details>
          )}
        </div>
      )}
    </section>
  )
}

export const SalesDeckRenderer: React.FC<SalesDeckRendererProps> = ({ deck }) => {
  const slides = [...deck.slides, deck.cta]
  const handlePrint = () => window.print()

  return (
    <div className="sales-deck-page">
      <header className="sales-deck-topbar">
        <a href="/sales-decks" className="sales-deck-back">Sales decks</a>
        <nav className="sales-deck-topbar__links" aria-label="Deck navigation">
          <a href="#cover">Cover</a>
          <a href={`#${getSlideAnchor(deck.cta)}`}>CTA</a>
          <button className="btn btn-primary sales-deck-print" type="button" onClick={handlePrint}>Print / Save as PDF</button>
        </nav>
      </header>

      <main className="sales-deck-main">
        <section className="sales-deck-cover" id="cover">
          <div className="sales-deck-cover__copy">
            <p className="sales-deck-kicker">{deck.targetBuyer}</p>
            <h1>{deck.title}</h1>
            <p className="sales-deck-subtitle">{deck.subtitle}</p>
            <p className="sales-deck-hero-message">{deck.heroMessage}</p>
          </div>
          <div className="sales-deck-cover__panel">
            <div>
              <span>Buyer pain</span>
              <p>{deck.buyerPainSummary}</p>
            </div>
            <div>
              <span>Core promise</span>
              <p>{deck.corePromise}</p>
            </div>
            <div>
              <span>Best use case</span>
              <p>{deck.bestUseCase}</p>
            </div>
          </div>
        </section>

        <nav className="sales-deck-anchor-grid" aria-label="Slide list">
          {slides.map((slide, index) => (
            <a href={`#${getSlideAnchor(slide)}`} key={slide.id}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              {slide.title}
            </a>
          ))}
        </nav>

        <div className="sales-deck-slides">
          {slides.map((slide, index) => renderSlide(slide, index, slides.length))}
        </div>
      </main>

      <footer className="sales-deck-footer">
        <a className="btn btn-secondary" href="/sales-decks">All decks</a>
        <a className="btn btn-ghost" href="#cover">Back to top</a>
        <button className="btn btn-primary" type="button" onClick={handlePrint}>Print / Save as PDF</button>
      </footer>
    </div>
  )
}
